import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { type Ref, ref, computed, watch, onMounted } from 'vue';
// Props
// ===============
type PosMap = {
  left: {
    raw: Ref<number>;
    prop: Ref<number>;
  };
  right: {
    raw: Ref<number>;
    prop: Ref<number>;
  };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterRange',
  props: {
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  left: {
    type: Number,
    default: 0,
  },
  right: {
    type: Number,
    default: 100,
  },
},
  emits: ["update:left", "update:right", "release"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:left', value: number): void;
  (e: 'update:right', value: number): void;
  (e: 'release'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;





// Slider bar
// =========================

const wrap: Ref<HTMLElement | null> = ref(null);

const sliderBarElement: Ref<HTMLElement | null> = ref(null);

const sliderbarWidth = computed(() => {
  return sliderBarElement.value?.offsetWidth ?? 0;
});

// Common
// ==========================

const stepLength = computed(() => {
  return (sliderbarWidth.value * props.step) / (props.max - props.min);
});

// Position
// ==========================

function truncValue(value: number) {
  if (value >= 0) {
    const width = sliderbarWidth.value;
    const truncValue = value <= width ? value : width;

    return truncValue;
  }

  return 0;
}

function calcValue(value: number) {
  return Math.floor(truncValue(value) / stepLength.value) * props.step + props.min;
}

function calcPos(value: number) {
  return (value / props.step) * stepLength.value;
}

const _leftPos = ref(calcPos(props.left));
const _rightPos = ref(calcPos(props.right));

const active: Ref<keyof PosMap | null> = ref(null);

function syncPos() {
  if (!active.value) {
    _leftPos.value = calcPos(props.left);
    _rightPos.value = calcPos(props.right);
  }
}

watch(props, () => syncPos());
onMounted(() => syncPos());

const leftPos = computed({
  get() {
    if (active.value) return truncValue(_leftPos.value);
    else return calcPos(props.left);
  },
  set(value) {
    _leftPos.value = value;
    emits('update:left', calcValue(value));
  },
});

const rightPos = computed({
  get() {
    if (active.value) return truncValue(_rightPos.value);
    else return calcPos(props.right);
  },
  set(value) {
    _rightPos.value = value;
    emits('update:right', calcValue(value));
  },
});

const map: PosMap = {
  left: {
    raw: _leftPos,
    prop: leftPos,
  },
  right: {
    raw: _rightPos,
    prop: rightPos,
  },
};

// Thumbs
// =========================

const thumbLeftElement: Ref<HTMLElement | null> = ref(null);

const thumbRightElement: Ref<HTMLElement | null> = ref(null);

function windowRelease() {
  syncPos();
  active.value = null;
  document.body.style.cursor = 'auto';
  if (wrap.value) wrap.value.style.cursor = 'grab';

  window.removeEventListener('mousemove', windowMove);
  window.removeEventListener('mouseup', windowRelease);
  emits('release');
}

function windowMove(e: MouseEvent) {
  const pos = e.movementX;
  if (!active.value) throw new Error('Active element cant be null!');

  map[active.value].prop.value = map[active.value].raw.value + pos;

  if (leftPos.value > rightPos.value) {
    const oldVal = rightPos.value;
    rightPos.value = leftPos.value;
    leftPos.value = oldVal;

    active.value = active.value === 'left' ? 'right' : 'left';
  }
}

function thumbHold(e: MouseEvent) {
  e.preventDefault();

  const pos = e.offsetX;
  const leftLength = Math.abs(pos - leftPos.value);
  const rightLength = Math.abs(pos - rightPos.value);

  // Set closest as active slider
  active.value = leftLength < rightLength ? 'left' : 'right';

  map[active.value].prop.value = pos;

  document.body.style.cursor = 'grabbing';
  if (wrap.value) wrap.value.style.cursor = 'grabbing';
  window.addEventListener('mousemove', windowMove);
  window.addEventListener('mouseup', windowRelease);
}

// Fillbar
// ===============

const fillBarStyle = computed(() => {
  return {
    left: `${leftPos.value}px`,
    width: `${fillBarWidth.value}px`,
  };
});

const fillBarWidth = computed(() => {
  return rightPos.value - leftPos.value;
});

function preventDrag(e: Event) {
  e.preventDefault();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "double-slider",
    onMousedown: thumbHold,
    ref_key: "wrap",
    ref: wrap
  }, [
    _createElementVNode("div", {
      class: "double-slider__bar",
      onMousedown: preventDrag,
      ref_key: "sliderBarElement",
      ref: sliderBarElement
    }, null, 544 /* HYDRATE_EVENTS, NEED_PATCH */),
    _createElementVNode("div", {
      class: "double-slider__fill-bar",
      onMousedown: preventDrag,
      style: _normalizeStyle(_unref(fillBarStyle))
    }, null, 36 /* STYLE, HYDRATE_EVENTS */),
    _createElementVNode("div", {
      class: "double-slider__thumb",
      onMousedown: preventDrag,
      style: _normalizeStyle({ left: `${_unref(leftPos)}px` }),
      ref_key: "thumbLeftElement",
      ref: thumbLeftElement
    }, null, 36 /* STYLE, HYDRATE_EVENTS */),
    _createElementVNode("div", {
      class: "double-slider__thumb",
      onMousedown: preventDrag,
      style: _normalizeStyle({ left: `${_unref(rightPos)}px` }),
      ref_key: "thumbRightElement",
      ref: thumbRightElement
    }, null, 36 /* STYLE, HYDRATE_EVENTS */)
  ], 544 /* HYDRATE_EVENTS, NEED_PATCH */))
}
}

})